// The Follow-up Settings form and the body PUT to v1/followupSettings.
//
// Decision 3 (14/10, backend BE7): no approval, no no-reply escalation, and a logged call or note never pauses or stops
// the emails (the backend accepts only onLoggedContact OFF). Those settings are no longer on the screen and not sent:
// the PUT is partial, so the stored values stay as they are (script 40 switched them off).
//
// "Chase quotes with these statuses" holds what the backend stores (status names, eligibleQuoteStatuses).
// Batch 3: which project types are chased is set per type on the Quote types tab (v1/followupTypes), so the old
// excludedProjectTypeIds list is no longer edited here and not sent (the PUT is partial: the saved list stays as the
// fallback for a type with no settings of its own).

export const WEEK_DAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' }
]

function list(v) {
  return Array.isArray(v) ? v.slice() : []
}

function text(v) {
  return v === null || v === undefined ? '' : String(v)
}

/** The saved settings as the form's model. */
export function toForm(settings) {
  var s = settings || {}
  return {
    id: s.id,
    enabled: !!s.enabled,
    workStart: text(s.workStart),
    workEnd: text(s.workEnd),
    workDays: list(s.workDays).map(Number),
    dailyCap: s.dailyCap === undefined ? null : s.dailyCap,
    chaseTaskDueWorkingDays: s.chaseTaskDueWorkingDays === undefined || s.chaseTaskDueWorkingDays === null ? 1 : s.chaseTaskDueWorkingDays,
    noContactReminderWorkingDays: s.noContactReminderWorkingDays === undefined || s.noContactReminderWorkingDays === null ? 3 : s.noContactReminderWorkingDays,
    warnDaysAhead: s.warnDaysAhead === undefined || s.warnDaysAhead === null ? 7 : s.warnDaysAhead,
    outboxWorkingDays: s.outboxWorkingDays === undefined || s.outboxWorkingDays === null ? 5 : s.outboxWorkingDays,
    internalRecipientOverride: text(s.internalRecipientOverride),
    excludedProjectTypeIds: list(s.excludedProjectTypeIds).map(Number),
    eligibleQuoteStatuses: list(s.eligibleQuoteStatuses).map(String),
    // on unless the backend says otherwise (its default is true)
    skipExpiredQuotes: s.skipExpiredQuotes !== false
  }
}

/**
 * The form as the PUT body. A multi-select may hold option objects if it was bound
 * without optionValue, so both shapes are reduced to the stored key. Only keys the backend
 * knows are sent (it answers 400 "Unknown settings" otherwise), so form-only fields stay out. A blank workStart
 * or workEnd is left out (the PUT is a partial update, so the saved time stays).
 */
export function toPayload(form) {
  var f = form || {}
  var statuses = list(f.eligibleQuoteStatuses).map(function (s) {
    return s && typeof s === 'object' ? s.statusName : s
  }).filter(function (s) { return typeof s === 'string' && s !== '' })
  var days = list(f.workDays).map(Number).filter(function (n) { return n >= 1 && n <= 7 })
  days.sort(function (a, b) { return a - b })
  var out = {
    enabled: !!f.enabled,
    workStart: text(f.workStart).trim(),
    workEnd: text(f.workEnd).trim(),
    workDays: days,
    dailyCap: f.dailyCap,
    chaseTaskDueWorkingDays: f.chaseTaskDueWorkingDays,
    noContactReminderWorkingDays: f.noContactReminderWorkingDays,
    warnDaysAhead: f.warnDaysAhead,
    outboxWorkingDays: f.outboxWorkingDays,
    internalRecipientOverride: text(f.internalRecipientOverride).trim(),
    eligibleQuoteStatuses: statuses,
    skipExpiredQuotes: f.skipExpiredQuotes !== false
  }
  // The backend rejects "" as a time; a key left out keeps the saved value
  if (out.workStart === '') delete out.workStart
  if (out.workEnd === '') delete out.workEnd
  return out
}

/**
 * A warning when follow-ups are (or would be) on but this server does not send them: its follow-up sending is OFF or
 * its send job is off. Switching on then records the go-live moment, and emails due before the server is changed are
 * skipped (phase3-update review). '' when nothing to warn about.
 */
export function serverSwitchWarning(settings) {
  if (!settings || settings.serverFollowupSend === undefined) return ''
  var off = []
  if (settings.serverFollowupSend === 'OFF') off.push('follow-up sending is OFF on this server')
  if (settings.serverSchedulerOn === false) off.push("this server's follow-up send job is off")
  var testOnly = settings.serverFollowupSend === 'OVERRIDE_ONLY'
    ? 'This server sends follow-up emails only to the test address (Send all follow-up emails to). ' : ''
  if (!off.length) return testOnly.trim()
  return testOnly + 'Note: ' + off.join(' and ') + ', so nothing goes out until TrustiTech changes the server. ' +
    'Switching on now records the go-live moment, and emails due before the server is changed are skipped.'
}

/** One line for the toast after "Run now": the pass's counts, then the backend's note if it sent one. */
export function runNowSummary(result) {
  var r = result || {}
  var n = function (v) { return typeof v === 'number' ? v : 0 }
  var line = 'Sent ' + n(r.sent) + ', not sent ' + n(r.skipped) + ', failed ' + n(r.failed) +
    ', stopped ' + n(r.stopped) + (n(r.reminders) ? ', reminder tasks ' + n(r.reminders) : '') +
    (n(r.paused) ? ', paused ' + n(r.paused) : '') +
    (n(r.stepReminders) ? ', reminders to salespeople ' + n(r.stepReminders) : '') + '.'
  return r.note ? line + ' ' + r.note : line
}

// P6 (29/09): the settings screen in tabs. Each tab: its key, header, one-line explanation and the settings
// on it (a failed save opens the first tab holding an error). The last two tabs hold the Steps and Bank
// Holidays screens, which save themselves.
export const SETTINGS_TABS = [
  {
    key: 'sending',
    header: 'Sending',
    explain: 'Whether follow-up emails go out, where to while testing, and when they are sent.',
    // "Company email domains for Outlook" was removed (Mohammed 04/10): users are switched on for Outlook one by one,
    // so the domain check adds nothing. The setting is never sent, so the stored value (blank = no check) stays.
    fields: ['enabled', 'internalRecipientOverride', 'workStart', 'workEnd', 'workDays', 'dailyCap']
  },
  {
    key: 'chased',
    header: 'Who gets chased',
    explain: 'Which quotes are enrolled and chased: by quote status, and whether expired quotes count. Quote types are on their own tab.',
    fields: ['eligibleQuoteStatuses', 'skipExpiredQuotes']
  },
  {
    key: 'types',
    header: 'Quote types',
    explain: 'Each quote type (OTP, Tender, Construction Tender, ...) can have emails on or off, its own steps and days, and its own no-reply rule. Anything not set uses the default.',
    fields: []
  },
  {
    key: 'escalation',
    header: 'Reminders',
    explain: 'The warning before a task or event clashes with an email, what Emails Going Out shows, and the reminder to the salesperson when nothing is logged after an email. The day before each email the salesperson is emailed its text, to edit, pause or stop it; if they do nothing it goes.',
    fields: ['warnDaysAhead', 'outboxWorkingDays', 'noContactReminderWorkingDays', 'chaseTaskDueWorkingDays']
  },
  {
    key: 'wording',
    header: 'Email wording',
    explain: 'The default emails, used by every quote type without steps of its own: one step per email, sent the given number of days after the quote is booked.',
    fields: []
  },
  {
    key: 'holidays',
    header: 'Bank holidays',
    explain: 'Days when no follow-up email is sent; an email due on one waits for the next working day.',
    fields: []
  }
]

/** The key of the first tab holding one of these field errors, or null. */
export function tabWithError(errors) {
  var keys = Object.keys(errors || {})
  var tab = SETTINGS_TABS.find(t => t.fields.some(f => keys.indexOf(f) > -1))
  return tab ? tab.key : null
}

/** The summary strip: "Off · 2 steps (2, 9 days)", from the saved settings and the steps. */
export function settingsSummary(settings, steps) {
  if (!settings) return ''
  var active = (steps || []).filter(s => s && s.active).sort((a, b) => a.stepNo - b.stepNo)
  var parts = [settings.enabled ? 'On' : 'Off']
  if (!active.length) parts.push('no steps')
  else {
    parts.push(active.length + (active.length === 1 ? ' step (' : ' steps (') +
      active.map(s => s.daysAfterEnrolment).join(', ') + ' days)')
  }
  var override = text(settings.internalRecipientOverride).trim()
  if (override) parts.push('all emails to ' + override + ' (testing)')
  return parts.join(' · ')
}
